import React, { useEffect, useState } from "react";
import { IconContext } from "react-icons";
import { MdArrowDownward } from "react-icons/md";
import { animated, useSpring } from "react-spring";
import EventFooter from "../eventFooter";

interface ILandingAnimateSplash {
  onScrollHandler: (index: number) => void;
}

const LandingAnimateSplash: React.FC<ILandingAnimateSplash> = (props) => {
  const { onScrollHandler } = props;

  // 新增：只有在需要時才載入影片，並偵測螢幕大小以載入對應檔案

  const [isLaptopViewport, setIsLaptopViewport] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : false
  );

  const desktopUrl = "https://dqyerp2t68wdu.cloudfront.net/top-banner-pc.mp4";
  const mobileUrl = "https://dqyerp2t68wdu.cloudfront.net/top-banner-m.mp4";

  const [isReady, setIsReady] = useState(false);
  // main logo animation times
  const [loopTimes, setLoopTimes] = useState(1);
  const [logoDone, setLogoDone] = useState(false);
  const [bgFadeInDone, setBgFadeInDone] = useState(false);
  const [fadeInDone, setFadeInDone] = useState(false);
  const [hideAnimation, setHideAnimation] = useState(false);

  const [showDonationPopup, setShowDonationPopup] = useState(true);

  // mainLogo flash animation
  const mainLogoFlashSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: 300 },
    reset: true,
    onRest: () => {
      if (loopTimes < 2) {
        setLoopTimes(loopTimes + 1);
      } else {
        setLogoDone(true);
      }
    },
    key: loopTimes,
  });

  // mainLogo fadeout animation
  const mainLogoDisapperSpring = useSpring({
    from: { opacity: 1 },
    to: { opacity: 0 },
    config: { duration: 1300 },
    delay: 1000,
    onRest: () => setLogoDone(true),
  });

  // bg fade in animation
  const bgFadeInSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: 1800 },
    delay: 2500,
    onRest: () => setBgFadeInDone(true),
  });

  // main fade in animation
  const fadeInSpring = useSpring({
    from: { opacity: 0 },
    to: bgFadeInDone ? { opacity: 1 } : null,
    config: { duration: 2000 },
    // delay: 4600,
    onRest: () => setFadeInDone(true),
  });

  // evLogo animation
  const evLogoSpring = useSpring({
    from: { opacity: 0 },
    to: fadeInDone ? { opacity: 1 } : null,
    config: { duration: 1200 },
    // delay: 4600,
  });

  const leftSpring = useSpring({
    from: { transform: "translate(0%, 0%)", opacity: 1 },
    to: { transform: "translate(-80%, 30%)", opacity: 0.3 },
    config: { duration: 3500 },
    delay: 2500,
  });

  const rightSpring = useSpring({
    from: { transform: "translate(0%, 0%)", opacity: 1 },
    to: { transform: "translate(30%, -80%)", opacity: 0.3 },
    config: { duration: 3500 },
    delay: 2500,
    onRest: () => setHideAnimation(true),
  });

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      // 新的/舊的 API 相容處理
      setIsLaptopViewport("matches" in e ? e.matches : mq.matches);

    // 兩種 API 支援
    if ("addEventListener" in mq) mq.addEventListener("change", handler);
    // @ts-ignore
    else mq.addListener(handler);

    return () => {
      if ("removeEventListener" in mq)
        mq.removeEventListener("change", handler);
      //@ts-ignore
      else mq.removeListener(handler);
    };
  }, []);

  // 延後到背景淡入完成再真正給 video source，以避免在動畫期間浪費頻寬

  useEffect(() => {
    setIsReady(true);
  }, []);

  if (!isReady) return null;
  return (
    <div className="relative w-full h-[100dvh] flex flex-col overflow-scroll bg-black">
      <div className="flex justify-between items-center m-6 laptop:m-10 z-10">
        {/* left top logo */}
        <animated.a style={evLogoSpring} href={"/"}>
          <img
            className="w-[8rem] h-[3rem]"
            src={require("../../../assets/images/asgard-ev-logo.png")}
            alt="asgard-ev-logo"
          />
        </animated.a>

        {/* right top logo */}
        <animated.img
          style={evLogoSpring}
          className="w-[120px] h-[32px]"
          src={require("../../../assets/images/asgard-ev-logo2.png")}
          alt="asgard-ev-logo2"
        />
      </div>

      {/* image version for desktop */}
      {/* <animated.img
        style={bgFadeInSpring}
        className="hidden laptop:flex absolute w-full h-full object-cover bg-black"
        src={require("../../../assets/images/top-banner-pc.png")}
      /> */}

      {/* video version for desktop */}
      <animated.div
        // style={bgFadeInSpring}
        className="hidden laptop:flex absolute w-full h-full bg-black"
      >
        <video
          className="w-full h-full object-cover opacity-60"
          muted
          loop
          playsInline
          autoPlay={isLaptopViewport} // only autoplay on matching viewport
          preload="none"
        >
          {/* only insert desktop source when we should load AND viewport is desktop */}
          {isLaptopViewport && <source src={desktopUrl} type="video/mp4" />}
        </video>
      </animated.div>

      <animated.div
        // style={bgFadeInSpring}
        className="flex laptop:hidden absolute w-full h-full bg-black"
      >
        <video
          className="w-full h-full object-cover opacity-60"
          autoPlay={!isLaptopViewport} // only autoplay on matching viewport
          loop
          muted
          playsInline
          preload="none"
        >
          {/* only insert mobile source when we should load AND viewport is mobile */}
          {!isLaptopViewport && <source src={mobileUrl} type="video/mp4" />}
        </video>
      </animated.div>

      {/* main logo */}
      {!hideAnimation && (
        <animated.img
          style={logoDone ? mainLogoDisapperSpring : mainLogoFlashSpring}
          className="w-[12.5rem] laptop:w-[31.25rem] h-auto laptop:h-[31.25rem] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50"
          // src={require("../../../assets/images/supercharge-logo.png")}
          src={require("../../../assets/images/gg.png")}
          alt="charge-logo"
        />
      )}
      {/* main fade spring - desktop */}
      {false && (
        <animated.img
          style={fadeInSpring}
          className="flex w-[60%] laptop:w-[20%] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          src={require("../../../assets/icons/animationLogo.png")}
          alt="main fade logo"
        />
      )}

      {/* <a
        href="https://onelink.to/9mmytd"
        target="_blank"
        referrerPolicy="no-referrer"
      >
        <animated.img
          style={evLogoSpring}
          className="w-[120px] laptop:w-[200px] absolute bottom-[17rem] laptop:bottom-[24rem] right-6 cursor-pointer z-30"
          src={require("../../../assets/images/googlePlay.png")}
          alt="google-play"
        />
      </a>

      <a
        href="https://onelink.to/9mmytd"
        target="_blank"
        referrerPolicy="no-referrer"
      >
        <animated.img
          style={evLogoSpring}
          className="w-[120px] laptop:w-[200px] absolute bottom-[14rem] laptop:bottom-[18rem] right-6 cursor-pointer z-30"
          src={require("../../../assets/images/appStore.png")}
          alt="appl-store"
        />
      </a> */}
      <div
        className="group absolute bottom-[10rem] laptop:bottom-[13rem] right-6 flex justify-center items-center bg-mainDark rounded-full w-14 h-14 hover:bg-mainBlue transition-all duration-500 ease-in-out cursor-pointer z-30"
        onClick={() => onScrollHandler(1)}
      >
        <IconContext.Provider
          value={{
            className:
              "fill-mainBlue group-hover:fill-black transition-all duration-500 ease-in-out",
          }}
        >
          <MdArrowDownward size={30} />
        </IconContext.Provider>
      </div>
      <EventFooter />
      {/* opening cover */}
      {!hideAnimation && (
        <div className="absolute top-0 left-0 w-full h-full z-40">
          <div className="relative">
            {/* right triangle */}
            <animated.div
              style={rightSpring}
              className="absolute w-0 h-0 border-l-[100vw] border-t-[100dvh] border-l-transparent border-t-[#111111]"
            ></animated.div>

            {/* left triangle */}
            <animated.div
              style={leftSpring}
              className="absolute w-0 h-0 border-r-[100vw] border-b-[100dvh] border-r-transparent border-b-[#111111]"
            ></animated.div>
          </div>
        </div>
      )}

      {/* donation popup */}
      {false && (
        <animated.div
          style={fadeInSpring}
          className="absolute w-[320px] h-[340px] laptop:w-[520px] laptop:h-[340px] z-40 text-white text-center rounded-2xl bg-gradient-to-br from-black/90 to-black/70 top-1/2 laptop:top-[45%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col justify-center items-center px-8 py-8 space-y-4 shadow-2xl"
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-2 hover:bg-mainBlue transition-colors"
            onClick={() => setShowDonationPopup(false)}
            aria-label="關閉"
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
          <div className="text-2xl laptop:text-3xl font-medium">
            12月1日至14日全線充電量
          </div>
          <div className="text-2xl laptop:text-3xl font-medium">
            所得收益，將全數捐出
          </div>
          <div className="text-xl laptop:text-lg">
            以每 <span className="font-bold text-mainLightBlue ">1kWh</span> 為{" "}
            <span className="font-bold text-mainLightBlue">$1</span>，
          </div>
          <div className="text-xl laptop:text-lg">
            用於支援{" "}
            <span className="font-bold text-mainLightBlue ">
              宏福苑災後援助工作
            </span>
            。
          </div>
        </animated.div>
      )}
    </div>
  );
};

export default LandingAnimateSplash;
